import React from 'react';
import { LineChartData } from '../../types';
import './LineChart.css';

interface LineChartProps {
  data: LineChartData[];
  title?: string;
  yLabel?: string;
}

export const LineChart: React.FC<LineChartProps> = ({ data, title, yLabel }) => {
  if (data.length === 0) {
    return (
      <div className="admin-line-chart">
        {title && <h3 className="admin-line-chart__title">{title}</h3>}
        <p className="admin-line-chart__empty">No data available</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const minValue = Math.min(...data.map(d => d.value), 0);
  const range = maxValue - minValue || 1;

  const svgWidth = 600;
  const svgHeight = 250;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartWidth = svgWidth - padding.left - padding.right;
  const chartHeight = svgHeight - padding.top - padding.bottom;

  // Calculate points
  const points = data.map((d, i) => ({
    x: padding.left + (i / Math.max(data.length - 1, 1)) * chartWidth,
    y: padding.top + chartHeight - ((d.value - minValue) / range) * chartHeight,
    value: d.value,
    label: d.label,
  }));

  // Build SVG path
  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ');

  // Area fill path
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;

  // Grid lines (5 horizontal)
  const gridLines = Array.from({ length: 5 }, (_, i) => {
    const y = padding.top + (i / 4) * chartHeight;
    const value = maxValue - (i / 4) * range;
    return { y, value: value.toFixed(2) };
  });

  // X-axis labels (show every Nth label to avoid crowding)
  const labelInterval = Math.max(1, Math.floor(data.length / 6));

  return (
    <div className="admin-line-chart">
      {title && <h3 className="admin-line-chart__title">{title}</h3>}
      <div className="admin-line-chart__container">
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="admin-line-chart__svg"
          role="img"
          aria-label={title || 'Line chart'}
        >
          {/* Grid lines */}
          {gridLines.map((line, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={line.y}
                x2={svgWidth - padding.right}
                y2={line.y}
                className="admin-line-chart__grid-line"
              />
              <text
                x={padding.left - 8}
                y={line.y + 4}
                className="admin-line-chart__y-label"
                textAnchor="end"
              >
                {line.value}
              </text>
            </g>
          ))}

          {/* Area fill */}
          <path d={areaPath} className="admin-line-chart__area" />

          {/* Line */}
          <path d={linePath} className="admin-line-chart__line" />

          {/* Data points */}
          {points.map((p, i) => (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={3}
              className="admin-line-chart__point"
            >
              <title>{`${p.label}: ${p.value}`}</title>
            </circle>
          ))}

          {/* X-axis labels */}
          {points.map((p, i) => {
            if (i % labelInterval !== 0 && i !== data.length - 1) return null;
            const label = p.label.length > 5 ? p.label.slice(5) : p.label;
            return (
              <text
                key={i}
                x={p.x}
                y={svgHeight - 8}
                className="admin-line-chart__x-label"
                textAnchor="middle"
              >
                {label}
              </text>
            );
          })}

          {/* Y-axis label */}
          {yLabel && (
            <text
              x={14}
              y={svgHeight / 2}
              className="admin-line-chart__axis-label"
              textAnchor="middle"
              transform={`rotate(-90, 14, ${svgHeight / 2})`}
            >
              {yLabel}
            </text>
          )}
        </svg>
      </div>
    </div>
  );
};
